import {PolymerElement} from '@polymer/polymer/polymer-element.js';
import {html} from '@polymer/polymer/lib/utils/html-tag.js';
import '@polymer/iron-ajax/iron-ajax.js';
import {ScanCardService} from '@nuskin/ns-shop';
import template from './ns-scan-card.html';
import {TranslationsUtil} from '@nuskin/ns-util';
import {Translations} from './translations.js';

/**
 * Image Imports
 */
import closeIcon from '../img/ic-close-small.svg';

/**
 * An element to handle display and actions around a scan card in an ADR order.
 *
 * <section>
 *  <h2>Content Guidelines</h2>
 *  <p>This component listens to a scanCardsDataObject property and populates it with
 *  necessary scan card information.</p>
 * </section>
 *
 * <h1>Scan Card Component Demo</h1>
 * <demo-snippet class="demoSnippet">
 *   <template>
 *     <ns-scan-cards limit="2" is-demo></ns-scan-cards>
 *   </template>
 * </demo-snippet>
 */
export class NsScanCard extends PolymerElement {
  static get is () {
    return 'ns-scan-card';
  }

  /**
   * Sets the html template
   */
  static get template () {
    let htmlElement = document.createElement('TEMPLATE');
    htmlElement.innerHTML = template;
    return htmlElement;
  }

  /**
   * Sets properties to pass to this component
   */
  static get properties () {
    return {
      /**
       * Country property
       *
       * Examples:
       *  - 'US', 'DE'
       */
      country: {
        type: String
      },
      /**
       * Language property.
       *
       * Examples:
       *  - 'en', 'es'
       */
      language: {
        type: String
      },
      /**
       * Translations data
       */
      translations: {
        type: JSON
      },
      /**
       * Scan cards temp data object, displayed in editing mode
       */
      tempData: {
        type: Object,
        value: []
      },
      /**
       * Error message to be displayed when saving fails
       */
      errorMessage: {
        type: String
      },
      /**
       * Object used to share data between ns-scan-cards and ns-adp-wrapper.
       */
      scanCardsDataObject: {
        type: Object,
        notify: true
      },
      /**
       * Encoded value of close icon used for passing this value to the template
       */
      closeIcon: {
        type: String,
        value: closeIcon
      },
      /**
       * Populates component with test data and prevents any unnecessary network calls.
       */
      isDemo: {
        type: Boolean,
        value: false
      },
      /**
       * Page upon which this component appears. Used for translations.
       */
      webPageName: {
        type: String,
        value: 'shop/adp-manage'
      },
      /**
       * Translations from the server. Triggers the translation assignment process.
       * If undefined and shouldAwaitTranslations=false, this component will fetch its own strings.
       */
      serverTranslations: {
        type: Object,
        observer: '_assignTranslations'
      },
      /**
       * If false, it will start the translation assignment process without waiting.
       * If true, it will wait for serverTranslations to be set before starting the translation assignment process.
       * If serverTranslations is defined already, the component will behave as if this property is true.
       */
      shouldAwaitTranslations: {
        type: Boolean,
        value: false
      },
      /**
       * Object used to share data between ns-scan-cards and ns-adp-wrapper.
       * @type {{ScanNumber?: string, Label?: string, Active: boolean, Action: string}}
       */
      scanCard: {
        type: Object,
        notify: true
      },
      /**
      * Flag to tell the component which view to use
      */
      isEditMode: {
        type: Boolean,
        notify: true,
        value: false
      }
    };
  }

  static get observers () {
    return [
      '_onCardNumberInput(scanCard.ScanNumber)',
      '_onCardLabelInput(scanCard.Label)',
      '_stopFlashTimer(shouldDeactivate)'
    ];
  }

  /**
   * This is a polymer lifecycle event.
   * Loads existing scan cards.
   */
  ready () {
    super.ready();
    if (!this.TRANSLATION_HELPER) this._localize();
    this.data = [];
    if (this.isDemo) {
      this.scanCardsDataObject = scanCardsTestObject;
    }
    this.set('isNewCard', !(this.scanCard && this.scanCard.ScanNumber && this.scanCard.Label));
    this.set('scanNumberDisabled', !this.isNewCard);
    if (this.isNewCard) {
      this.scanCard.Label = '';
      this.scanCard.ScanNumber = '';
      this.scanCard.Action = 'ADD';
    } else {
      this.scanCard.Action = 'CHANGE';
    }
    this.scanCard.Active = true;
  }

  /**
   * Sets language and country properties and prepares translations
   */
  _localize () {
    this.TRANSLATION_HELPER = new TranslationsUtil(this.language, this.country, Translations);
    this.language = this.TRANSLATION_HELPER.language;
    this.country = this.TRANSLATION_HELPER.country;
    if (!this.shouldAwaitTranslations) this._assignTranslations();
  }

  /**
   * Observer on serverTranslations that gathers all translations and maps them to properties.
   * Any strings set as properties and defined in the html attributes take precedence over translations from the server,
   * which in turn take precedence over the default translations in the translations file.
   */
  async _assignTranslations () {
    if (!this.TRANSLATION_HELPER) this._localize();
    this.serverTranslations = this.serverTranslations || await this.TRANSLATION_HELPER.getServerTranslations(this.webPageName);
    let allTranslations = Object.assign({}, this.TRANSLATION_HELPER.getDefaultTranslations(), this.serverTranslations);
    Object.entries(allTranslations).forEach(([key, value]) => {
      this[key] = this[key] || value;
    });
  }

  _emptyFields () {
    this.scanCard.ScanNumber = '';
    this.scanCard.Label = '';
  }

  _onCardLabelInput() {
    if (!this.scanCard.Label && !this.scanCard.ScanNumber) {
      this._setScanLabelValidity(true);
      this._setScanNumberValidity(true);
    }
  }

  _onCardNumberInput() {
    if (!this.scanCard.Label && !this.scanCard.ScanNumber) {
      this._setScanLabelValidity(true);
      this._setScanNumberValidity(true);
    } else {
      this._validateScanNumber();
    }
  }

  _validateRequiredFields() {
    const scanLabel = this.scanCard.Label,
          scanNumber = this.scanCard.ScanNumber;

    // Logical XNOR - label and number both present or absent
    if (scanLabel ? !!scanNumber : !scanNumber) {
      this._setScanLabelValidity(true);
      // Scan number validity is determined by server validation check
    }
    // Scan number is present but label is absent
    else if (!scanLabel) {
      this._setScanLabelValidity(true);
      this._setScanLabelValidity(false, this.noCardholderNameErrorMessage);
    }
    // Scan label is present but number is absent
    else if (!scanNumber) {
      this._setScanNumberValidity(true);
      this._setScanNumberValidity(false);
    }
  }

  _validateScanNumber() {
    if (!this.scanCard.ScanNumber) {
      this._setScanNumberValidity(false);
      return;
    };

    // Perform debounced server-side validation check
    clearTimeout(this.validationTimeout);
    this.validationTimeout = setTimeout(() => {
      ScanCardService.validateScanCard(this.scanCard.ScanNumber, response => {
        this._setScanNumberValidity(response.valid);
      });
    }, 500);
  }

  /**
   * 
   * @param {boolean} isValid 
   */
  _setScanNumberValidity(isValid) {
    this.set('cardNumberInvalid', !isValid);
    this.set('cardNumberErrorMessage', isValid ? '' : this.invalidCardNumberErrorMessage);
  }

  /**
   * 
   * @param {boolean} isValid 
   * @param {string} message 
   */
  _setScanLabelValidity(isValid, message) {
    this.set('cardLabelInvalid', !isValid);
    this.set('cardLabelErrorMessage', isValid ? '' : message);
  }

  _deactivateScanCard () {
    if (this.isDemo) {
      return;
    }
    if (this.shouldDeactivate) { // deactivate scan card
      this._closeDeactivateScanCardDialog();
      let scanCardRow = {};
      scanCardRow.ScanNumber = this.scanCard.ScanNumber;
      scanCardRow.Label = this.scanCard.Label;
      scanCardRow.Active = false;
      scanCardRow.Action = 'DEACTIVATE'
      this.scanCardsDataObject.scanCards = [scanCardRow];
      this.set('scanCardsDataObject.updateType', 'Deactivate');
      this._emptyFields();
    } else {
      if (this.flashTimer === null) {
        //setInterval to flash label and checkbox
        //setTimeout clears interval after 3 flashes
        this.flashTimer = setInterval(() => {
          this.hideDeactivateCheckbox = !this.hideDeactivateCheckbox
        },200);
        setTimeout(() => {
          clearInterval(this.flashTimer);
          this.flashTimer = null;
        }, 1200);
      }
    }
  }

  _closeDeactivateScanCardDialog () {
    this.shouldDeactivate = false;
    this.$.deactivateScanCardDialog.close();
    this.dispatchEvent(new CustomEvent('closeAdpWrapperOverlay', {detail : {closeOverlay: true}}));
  }

  _stopFlashTimer () {
    clearInterval(this.flashTimer);
    this.flashTimer = null;
  }

  _showDeactivateScanCardDialog () {
    this._stopFlashTimer();
    this.dispatchEvent(new CustomEvent('closeAdpWrapperOverlay', {detail : {closeOverlay: false}}));
    this.$.deactivateScanCardDialog.open();
  }

  validate() {
    if (!this.isEditMode) {
      return true;
    }

    this._validateRequiredFields();

    return !this.cardNumberInvalid && !this.cardLabelInvalid;
  }
}

customElements.define(NsScanCard.is, NsScanCard);
